// eslint-disable-next-line import/no-named-default
import { default as MuiButton, ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

/**
 * The app's button: MUI's Button plus a loading state (an inline spinner and a
 * disabled control while a request is in flight).
 *
 * `variant` is NOT defaulted — every call site picks one of the theme variants
 * (`primary` / `secondary` / `tertiary` / `ghost` / `tonal` / `contained`).
 * Loading is orthogonal to the variant, so it works on all of them.
 *
 * Geometry, type and the focus-outline reset live in the theme
 * (themes/componentOverrides → MuiButton), not here.
 */
export interface AppButtonProps extends ButtonProps {
  /** Required — pick a theme variant; there is no default. */
  variant: ButtonProps['variant'];
  /** Convenience alias of `onClick` that takes no event argument. */
  handleClick?: () => void;
  /** Show the spinner and disable the button. */
  isLoading?: boolean;
  /** Disable without the loading affordance. */
  isDisabled?: boolean;
}

function AppButton(props: AppButtonProps) {
  const {
    handleClick,
    isLoading = false,
    isDisabled = false,
    children,
    onClick,
    disabled,
    ...rest
  } = props;

  return (
    <MuiButton
      onClick={onClick ?? handleClick}
      disabled={disabled || isDisabled || isLoading}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
      {isLoading ? (
        <CircularProgress
          color="inherit"
          size="1rem"
          sx={{ marginLeft: '8px' }}
        />
      ) : null}
    </MuiButton>
  );
}

export default AppButton;
